import { useEffect, useRef } from "react";
import { anexoUrlAbsoluto } from "../services/tarefas";

interface AvatarProps {
  nome?: string | null;
  fotoUrl?: string | null;
  tamanho?: number; // px
  className?: string;
  /** Cor de fundo do fallback. Tailwind classes. */
  bgFallback?: string;
  /** Cor do texto do fallback. */
  textFallback?: string;
  /** Anel/borda. */
  ring?: string;
}

export function Avatar({
  nome,
  fotoUrl,
  tamanho = 32,
  className = "",
  bgFallback = "bg-blue-500",
  textFallback = "text-white",
  ring,
}: AvatarProps) {
  const inicial = (nome || "?").trim().charAt(0).toUpperCase();
  const baseClasses = `rounded-full overflow-hidden flex items-center justify-center font-bold shrink-0 ${bgFallback} ${textFallback} ${ring || ""} ${className}`;
  const style = { width: tamanho, height: tamanho, fontSize: Math.max(10, tamanho * 0.4) };

  if (fotoUrl) {
    const url = anexoUrlAbsoluto(fotoUrl);
    // GIF como foto de perfil deve aparecer ESTÁTICO. Renderiza via canvas
    // pra capturar só o primeiro frame; sem isso o navegador anima sozinho.
    const ehGif = /\.gif(\?|$)/i.test(url);
    if (ehGif) {
      return <AvatarCanvas url={url} nome={nome} baseClasses={baseClasses + " object-cover"} style={style} />;
    }
    return (
      <img
        src={url}
        alt={nome || "avatar"}
        className={baseClasses + " object-cover"}
        style={style}
        onError={(e) => {
          // se a imagem quebra, fallback para inicial
          (e.target as HTMLImageElement).style.display = "none";
          (e.target as HTMLImageElement).parentElement?.classList.add("show-fallback");
        }}
      />
    );
  }
  return (
    <div className={baseClasses} style={style}>{inicial}</div>
  );
}

function AvatarCanvas({ url, nome, baseClasses, style }: { url: string; nome?: string | null; baseClasses: string; style: React.CSSProperties }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const img = new window.Image();
    img.onload = () => {
      const c = canvasRef.current;
      if (!c) return;
      // Renderiza no tamanho exato do avatar (em pixels). object-cover via crop manual.
      const targetW = typeof style.width === "number" ? style.width : 32;
      const targetH = typeof style.height === "number" ? style.height : 32;
      c.width = targetW;
      c.height = targetH;
      const ctx = c.getContext("2d");
      if (!ctx) return;
      // Cover: escala mantendo aspect ratio e centraliza (crop nas bordas).
      const sw = img.naturalWidth;
      const sh = img.naturalHeight;
      const scale = Math.max(targetW / sw, targetH / sh);
      const dw = sw * scale;
      const dh = sh * scale;
      const dx = (targetW - dw) / 2;
      const dy = (targetH - dh) / 2;
      ctx.drawImage(img, dx, dy, dw, dh);
    };
    img.src = url;
  }, [url, style.width, style.height]);

  return (
    <canvas
      ref={canvasRef}
      aria-label={nome || "avatar"}
      title={nome || undefined}
      className={baseClasses}
      style={style}
    />
  );
}
